import TextField from '@components/v2/form/TextField/TextField';
import Icon from '@components/v2/ui/Icon/Icon';
import Modal from '@components/v2/ui/Modal/Modal';
import Typography from '@components/v2/ui/Typography/Typography';
import { InputAdornment, Stack } from '@mui/material';
import { Search } from 'lucide-react';
import { ChangeEvent, ReactNode, useRef } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { UserSelectModalTestIds } from '../../../../utils/testIds/SelectModals.testIds';
import { useTranslation } from '../../../translation/TranslationContext';
import { User } from '../../../user-admin/user.types';
import SelectModalList from '../SelectModalList/SelectModalList';
import { SELECT_MODAL_HEIGHT } from '../SelectModalList/selectModalList.constants';
import useUserSelectModal from './hooks/useUserSelectModal';

type UserSelectModalProps = {
  title: LangEnum | ReactNode;
  open: boolean;
  onClose: () => void;
  onClick: (user: User) => void;
};

const UserSelectModal = ({ title, open, onClose, onClick }: UserSelectModalProps) => {
  const { translate } = useTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const {
    items,
    isListLoading,
    hasNextPage,
    infiniteRef,
    rootRef,
    searchTerm,
    setSearchTerm,
    resetSearchTerm,
  } = useUserSelectModal({ open, onClick });

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      size="compact"
      height={SELECT_MODAL_HEIGHT}
      bodyRef={rootRef}
      onEntered={() => searchInputRef.current?.focus()}
      onExited={resetSearchTerm}
      header={{ title }}
      slots={{
        headerBottom: (
          <Stack sx={{ gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {translate(LangEnum.TEAM_MODAL_CHOOSE_USER_TEXT)}
            </Typography>
            <TextField
              ref={searchInputRef}
              value={searchTerm}
              placeholder={translate(LangEnum.GLOBAL_SEARCH)}
              testId={UserSelectModalTestIds.SEARCH_INPUT}
              onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                setSearchTerm(event.target.value)
              }
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon={Search} size="sm" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>
        ),
      }}
    >
      <SelectModalList
        items={items}
        isLoading={isListLoading}
        emptyMessage={LangEnum.GLOBAL_SEARCH_EMPTY_RESULTS}
        hasNextPage={hasNextPage}
        infiniteRef={infiniteRef}
      />
    </Modal>
  );
};

export default UserSelectModal;
